import { PluginListenerHandle } from "@capacitor/core";
import { PushNotificationSchema } from "@capacitor/push-notifications";
import React, { createContext, useCallback, useEffect, useState } from "react";

import { Analytics, AnalyticsEvents } from "modules/analytics";
import { isAnonymousUser } from "modules/anonymousBrowsing";
import { useUser } from "modules/user";
import { getCurrentPlatform } from "utils/platform.helpers";

import { PushNotificationActionPerformed } from "../IPushNotificationAdapter";
import { PushNotificationService } from "../PushNotificationService/PushNotificationService";
import { useDeepLinkNavigation } from "../useDeepLinkNavigation/useDeepLinkNavigation";
import { extractNotificationUrl } from "../utils/extractNotificationUrl";

type NotificationProviderProps = {
  readonly children: React.ReactNode;
  readonly isNativeMobileApp: boolean;
  readonly pushNotificationService: PushNotificationService;
};

type PushNotificationContext = {
  readonly pushNotifications: PushNotificationSchema[];
  readonly actionsPerformed: PushNotificationActionPerformed<unknown>[];
  readonly deviceToken?: string;
  /**
   * The one place the app asks the OS for push permission, shared by the automatic ask on Home
   * and the arrival-reminder primer, so a patient is never prompted twice from two code paths.
   * Resolves true when granted; registers the device on a grant and records a refusal.
   */
  readonly requestPermission: () => Promise<boolean>;
  /** Opens this app's page in the OS Settings, where a refused permission can be turned back on. */
  readonly openAppSettings: () => void;
};

const trackUserDeviceToken = (deviceToken: string, userId: string): void => {
  const options = {
    context: {
      userId,
      app: {
        version: import.meta.env.REACT_APP_VERSION,
      },
      device: {
        token: deviceToken,
        type: getCurrentPlatform(),
      },
    },
  };

  Analytics.track(AnalyticsEvents.DEVICE_CREATED_OR_UPDATED, {}, options);
  Analytics.track(AnalyticsEvents.DEVICE_TOKEN_REGISTERED, {}, options);
};

const trackNotificationPermissionDenied = (): void => {
  Analytics.track(AnalyticsEvents.PUSH_NOTIFICATION_PERMISSIONS_DENIED, {});
};

const trackNotificationRegistrationFailed = (): void => {
  Analytics.track(AnalyticsEvents.PUSH_NOTIFICATION_REGISTRATION_FAILED, {
    platform: getCurrentPlatform(),
  });
};

const trackNotificationReceived = (notification: PushNotificationSchema, userId: string): void => {
  const deliveryId = notification.data?.["CIO-Delivery-ID"];
  const deliveryToken = notification.data?.["CIO-Delivery-Token"];

  Analytics.reportPushMetric("delivered", deliveryId, deliveryToken);

  Analytics.track(
    AnalyticsEvents.PUSH_NOTIFICATION_RECEIVED,
    {
      notificationId: notification.id,
      title: notification.title,
      body: notification.body,
      platform: getCurrentPlatform(),
    },
    {
      context: {
        userId,
      },
    },
  );
};

const trackNotificationClicked = (
  action: PushNotificationActionPerformed<unknown>,
  userId: string,
): void => {
  const { notification, actionId } = action;

  const url = extractNotificationUrl(notification);
  const deliveryId = notification.data?.["CIO-Delivery-ID"];
  const deliveryToken = notification.data?.["CIO-Delivery-Token"];

  // Move Customer.io's own `opened` metric for this delivery. The analytics track below records the
  // tap for our reporting, but it does NOT mark the delivery opened in Customer.io — the broadcast's
  // "has opened the push" branch reads the delivery metric, so without this the SMS fallback fires
  // for everyone who opened the push.
  Analytics.reportPushMetric("opened", deliveryId, deliveryToken);

  Analytics.track(
    AnalyticsEvents.PUSH_NOTIFICATION_CLICKED,
    {
      notificationId: notification.id,
      actionId,
      title: notification.title,
      body: notification.body,
      url,
      platform: getCurrentPlatform(),
      deliveryId,
      deliveryToken,
    },
    {
      context: {
        userId,
      },
    },
  );
};

export default function PushNotificationProvider({
  children,
  isNativeMobileApp,
  pushNotificationService,
}: NotificationProviderProps): JSX.Element {
  const { handleNotificationAction } = useDeepLinkNavigation();

  const [pushNotifications, setPushNotifications] = useState<PushNotificationSchema[]>([]);
  const [actionsPerformed, setActionsPerformed] = useState<
    PushNotificationActionPerformed<unknown>[]
  >([]);
  const [deviceToken, setDeviceToken] = useState<string>();

  const { user } = useUser();

  const requestPermission = useCallback(async (): Promise<boolean> => {
    const permissionsGranted = await pushNotificationService.requestPermission();

    if (permissionsGranted) {
      await pushNotificationService.register();
    } else {
      trackNotificationPermissionDenied();
    }

    return permissionsGranted;
  }, [pushNotificationService]);

  const openAppSettings = useCallback(
    () => pushNotificationService.openAppSettings(),
    [pushNotificationService],
  );

  useEffect(() => {
    // Synchronize the authenticated identity and native push listeners before Capacitor registers.
    if (!isNativeMobileApp || !user?.id || isAnonymousUser(user)) {
      return () => undefined;
    }

    let canceled = false;
    let activeListeners: PluginListenerHandle[] = [];
    const userId = user.id;

    Analytics.identify(userId, {});

    const registerListeners = async (): Promise<PluginListenerHandle[]> => {
      const [
        onNotificationListener,
        onTokenListener,
        onActionListener,
        onRegistrationErrorListener,
      ] = await Promise.all([
        pushNotificationService.onNotificationReceived((notification) => {
          console.log("[RMD]: onNotificationReceived", notification);
          setPushNotifications((prev) => [...prev, notification]);
          trackNotificationReceived(notification, userId);
        }),
        pushNotificationService.onTokenReceived((token) => {
          setDeviceToken(token.value);
          Analytics.rememberPushDevice(userId, token.value);
          trackUserDeviceToken(token.value, userId);
        }),
        pushNotificationService.onNotificationActionPerformed((action) => {
          console.log("[RMD]: onNotificationActionPerformed", action);
          setActionsPerformed((prev) => [...prev, action]);
          trackNotificationClicked(action, userId);
          void handleNotificationAction(action);
        }),
        pushNotificationService.onRegistrationError(() => {
          trackNotificationRegistrationFailed();
        }),
      ]);

      return [
        onNotificationListener,
        onTokenListener,
        onActionListener,
        onRegistrationErrorListener,
      ];
    };

    const initializePushNotifications = async (): Promise<void> => {
      try {
        await pushNotificationService.register();
        console.log(
          "[RMD]: Registered for push notifications (will retrieve token if permissions already granted)",
        );
      } catch (error) {
        console.log(
          "[RMD]: Initial registration attempt failed (likely no permissions yet)",
          error,
        );
      }

      await requestPermission();
    };

    const synchronizePushNotifications = async (): Promise<void> => {
      const listeners = await registerListeners();

      if (canceled) {
        for (const l of listeners) {
          l.remove();
        }
        return;
      }

      activeListeners = listeners;
      await initializePushNotifications();
    };

    void synchronizePushNotifications();

    return () => {
      canceled = true;
      for (const listener of activeListeners) {
        listener.remove();
      }
    };
  }, [
    handleNotificationAction,
    isNativeMobileApp,
    pushNotificationService,
    requestPermission,
    user?.id,
  ]);

  const value = {
    pushNotifications,
    deviceToken,
    actionsPerformed,
    requestPermission,
    openAppSettings,
  };

  return (
    <PushNotificationContext.Provider value={value}>{children}</PushNotificationContext.Provider>
  );
}

export const PushNotificationContext = createContext<PushNotificationContext>({
  pushNotifications: [],
  actionsPerformed: [],
  deviceToken: undefined,
  // Outside the provider (web, tests) there is no OS to ask.
  requestPermission: async () => false,
  openAppSettings: () => undefined,
});
